import React from 'react';
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {vscDarkPlus} from "react-syntax-highlighter/dist/cjs/styles/prism";

const RetrieveMemory = ({ onSectionChange }) => {
  return (
    <div className="doc-content">
      <div className="doc-header">
        <h1>Retrieve Memory</h1>
        <p className="doc-description">
          Discover MemU's powerful memory retrieval system. Learn how to efficiently search and access stored memories using three specialized retrieval methods designed for different use cases.
        </p>
      </div>

      <div className="doc-section">
        <h2 id="overview">Overview</h2>
        <p>
          MemU provides three distinct methods for retrieving memories, each optimized for specific scenarios and performance requirements.
          These methods range from basic category-based retrieval to advanced semantic search capabilities.
        </p>
      </div>

      <div className="doc-section">
        <h2 id="retrieval-methods">Retrieval Methods</h2>

        <div className="method-card">
          <div className="method-header">
            <h3>1. Default Categories Retrieval</h3>
            <div className="method-badges">
              <span className="method-badge recommended">⭐⭐⭐ Most Basic & Highly Recommended</span>
            </div>
          </div>

          <div className="method-content">
            <p><strong>Function:</strong> <code className="function-highlight">retrieve_default_categories()</code></p>

            <div className="method-description">
              <h4>Key Features:</h4>
              <ul>
                <li>Focuses on core memory categories: <strong>profile</strong> and <strong>events</strong></li>
                <li>Uses predefined templates to extract personal information, automatically integrating career details from conversations like "Software Engineer at TechCorp"</li>
                <li>Ensures rapid access to essential information while avoiding redundant computations</li>
              </ul>
            </div>

            <div className="method-usage">
              <p><strong>When to Use:</strong> During system initialization or session startup (Only once)</p>
              <p><strong>Latency:</strong> ~50ms</p>
            </div>

            <div className="code-block">
              <SyntaxHighlighter
                  language="python"
                  style={vscDarkPlus}
                  customStyle={{
                    margin: 0,
                    padding: 0,
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}
              >
{`default_categories = memu_client.retrieve_default_categories(
    user_id="user_123",
    agent_id="assistant_001"
)

system_prompt = "You are a helpful AI assistant. Here's what you know about the user:\\n\\n"
for category in default_categories.categories:
    if category.summary:
        system_prompt += f"**{category.name}:** {category.summary}\\n\\n"`}
              </SyntaxHighlighter>
            </div>
          </div>
        </div>

        <div className="method-card">
          <div className="method-header">
            <h3>2. Related Categories Retrieval</h3>
            <div className="method-badges">
              <span className="method-badge advanced">⭐⭐ Advanced Clustering</span>
            </div>
          </div>

          <div className="method-content">
            <p><strong>Function:</strong> <code className="function-highlight">retrieve_related_clustered_categories(category_query)</code></p>

            <div className="method-description">
              <h4>Key Features:</h4>
              <ul>
                <li>Uses embedding similarity to dynamically identify top-k semantically related categories</li>
                <li>Analyzes conversation context through LLM, connecting "marathon training" discussions to "health status" and "daily routine" categories</li>
                <li>Excludes basic categories (profile/event/activity), focusing on specialized domain knowledge discovery</li>
              </ul>
            </div>

            <div className="method-usage">
              <p><strong>When to Use:</strong> For finding semantically related topics (system prompt) or advanced semantic search (user queries)</p>
              <p><strong>Latency:</strong> ~200ms</p>
            </div>

            <div className="code-block">
              <SyntaxHighlighter
                  language="python"
                  style={vscDarkPlus}
                  customStyle={{
                    margin: 0,
                    padding: 0,
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}
              >
{`# Retrieve categories related to a specific topic
related_categories = memu_client.retrieve_related_clustered_categories(
    user_id="user_123",
    agent_id="assistant_001",
    category_query="fitness and health activities"
)

# Process related categories
for category in related_categories.clustered_categories:
    print(f"Category: {category.name}")
    print(f"Relevance Score: {category.similarity_score}")
    print(f"Summary: {category.summary}")`}
                </SyntaxHighlighter>
              </div>
            </div>
          </div>
        </div>

        <div className="method-card">
          <div className="method-header">
            <h3>3. Related Memory Items Retrieval</h3>
            <div className="method-badges">
              <span className="method-badge contextual">⭐⭐ Context-Specific Retrieval</span>
            </div>
          </div>

          <div className="method-content">
            <p><strong>Function:</strong> <code className="function-highlight">retrieve_related_memory_items(query, [include_categories])</code></p>

            <div className="method-description">
              <h4>Key Features:</h4>
              <ul>
                <li>Extracts top-k memory entries based on embedding-based ranking</li>
                <li>Supports cross-category retrieval, linking "Sarah mentioned Mark" conversations to "physiotherapist" memories from different sessions</li>
                <li>Employs dynamic threshold mechanisms, automatically triggering A6 agent to generate new queries when retrieval results are insufficient</li>
              </ul>
            </div>

            <div className="method-usage">
              <p><strong>When to Use:</strong> For answering specific queries about past events or building context-aware responses</p>
              <p><strong>Latency:</strong> ~200ms</p>
            </div>

            <div className="code-block">
              <SyntaxHighlighter
                  language="python"
                  style={vscDarkPlus}
                  customStyle={{
                    margin: 0,
                    padding: 0,
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}
              >
{`# Retrieve specific memory items related to a query
related_memories = memu_client.retrieve_related_memory_items(
    user_id="user_123",
    query="What did Sarah say about her physiotherapist?",
    top_k=10
)

# Process retrieved memories
for memory in related_memories.related_memories:
    print(f"Memory: {memory.memory}")
    print(f"Relevance: {memory.similarity_score}")`}
              </SyntaxHighlighter>
            </div>
          </div>
        </div>

      <div className="doc-section">
        <h2 id="use-cases">Example Use Cases</h2>
        <p>
          Here are practical scenarios demonstrating when and how to use each retrieval method effectively.
        </p>

        <div className="use-cases-grid">
          <div className="use-case-card">
            <h4>📋 System Initialization</h4>
            <p><strong>Method:</strong> <code>retrieve_default_categories()</code></p>
            <p>Load essential user profile and event information at the start of each conversation session to provide personalized context.</p>
          </div>

          <div className="use-case-card">
            <h4>🔍 Topic Exploration</h4>
            <p><strong>Method:</strong> <code>retrieve_related_clustered_categories()</code></p>
            <p>Discover related categories and topics for comprehensive subject exploration.</p>
          </div>

          <div className="use-case-card">
            <h4>💬 Context-Aware Responses</h4>
            <p><strong>Method:</strong> <code>retrieve_related_memory_items()</code></p>
            <p>Retrieve specific memories to answer detailed questions about past conversations, events, or mentioned individuals.</p>
          </div>

          <div className="use-case-card">
            <h4>📅 Timeline Building</h4>
            <p><strong>Method:</strong> <code>retrieve_related_memory_items()</code></p>
            <p>Construct chronological sequences of events by retrieving memories related to specific topics or time periods.</p>
          </div>
        </div>
      </div>

    </div>
  );
};

export default RetrieveMemory;

